import { get } from '@lumapps/constants';
import { GetURLOptions, NavigationOptions } from '@lumapps/constants/types';
import { AppId, ExternalAppId, getAppIdFromUrl, Route, URL_PREFIX, URL_PREFIX_BY_APP_ID } from '@lumapps/router';
import { OnAfterRouteHook, OnBeforeRouteHook, RouterApi } from '@lumapps/router/api';
import {
    getRelativeRouteFromUrl,
    isInternalUrl,
    stripAnyAppPrefixFromUrl,
    addAppPrefixToUrl,
    redirectAbsoluteUrl,
    addFrontVersionToUrl,
    addQueryParamsToUrl,
} from '@lumapps/router/utils';

import {
    BKPR_PREFIX,
    PREFIXES_FOR_SUPPORTED_ROUTES_LIST,
    SUPPORTED_URLS_BY_APPS,
    APPS_PREFIX_MAPPINGS,
    PREFIXES_FOR_MAPPED_ROUTES_LIST,
    MAPPED_ROUTES,
} from '../constants';
import { isBkprUrl } from './isBkprUrl';
import { mapToBeekeeperRoute } from './mapToBeekeeperRoute';
import { stripBasePath } from './stripBasePath';

const { applicationId, isDev } = get();

export interface InitRouterBridgeProps {
    instanceSlug: string;
    customerSlug: string;
    isDevEnv?: boolean;
    router: RouterApi;
    getCurrentRoute: () => { currentRoute: Route; previousRoute: Route | undefined };
}

export interface PushOptions {
    redirect: RouterApi['redirect'];
    path: string;
    redirectAbsoluteUrl: (url: string, options?: NavigationOptions) => void;
    redirectToBeekeeper: (url: string, options?: NavigationOptions) => void;
    instanceSlug: string;
    customerSlug: string;
    replace?: boolean;
    origin?: string;
    options?: NavigationOptions;
}

export const doesUrlHaveAMappedUrl = (path: string, pathWithoutNoAppsPrefix: string, targetAppId: AppId) => {
    /**
     * If the URL is a Beekeeper URL, we need to check if the route is supported in the current application.
     * To check if it is supported, we look at the list of supported routes and see if the current route starts with one of them.
     */
    const supportedRoute =
        isBkprUrl(path) && PREFIXES_FOR_SUPPORTED_ROUTES_LIST.find((route) => pathWithoutNoAppsPrefix.includes(route));

    /**
     * We also want to make sure that the supported route exists in the target application.
     */
    const doesSupportedRouteExistInTargetApp = supportedRoute
        ? SUPPORTED_URLS_BY_APPS[supportedRoute]?.includes(targetAppId)
        : false;

    return {
        doesSupportedRouteExistInTargetApp,
        supportedRoute,
    };
};

export const parseUrl = (path: string, instanceSlug: string, customerSlug: string) => {
    /**
     * When the URL is internal, we need to check if it's a Beekeeper URL or not.
     * If it's a Beekeeper URL, we need to check if the route is supported in the current application.
     * If it's supported, we redirect using the router.
     * If it's not supported, we redirect to Beekeeper.
     */
    const isBkprPath = isBkprUrl(path);
    /**
     * Remove the base path and Beekeeper prefix (if applicable) to get the relative path
     * This converts URLs such as /home/ls/bk/tasks to /ls/tasks
     */
    const pathWithoutBasePath = stripBasePath(path, instanceSlug, customerSlug, isBkprPath);
    /**
     * Further remove any app prefixes as well
     * This converts URLs such as /ls/tasks to /tasks
     */
    const pathWithoutNoAppsPrefix = stripAnyAppPrefixFromUrl(pathWithoutBasePath);
    /**
     * From the path without the base path, we can get the target application ID
     * by looking at the prefixes applied.
     */
    const targetAppId = getAppIdFromUrl(pathWithoutBasePath);

    return {
        isBkprPath,
        pathWithoutBasePath,
        pathWithoutNoAppsPrefix,
        targetAppId,
    };
};

export const getMappedRoute = (targetAppId: AppId, pathWithoutBasePath: string, pathWithoutNoAppsPrefix: string) => {
    const relativeRoute = getRelativeRouteFromUrl(
        addAppPrefixToUrl(pathWithoutBasePath, targetAppId),
        targetAppId,
    ) as Route;

    const mappedRouteExists = PREFIXES_FOR_MAPPED_ROUTES_LIST.find((route) => pathWithoutNoAppsPrefix.includes(route));

    if (mappedRouteExists) {
        const mappedRoute = MAPPED_ROUTES[mappedRouteExists](pathWithoutBasePath);

        return mappedRoute;
    }

    return relativeRoute;
};

/**
 * Internal push function to handle Beekeeper routes.
 * @param redirect
 * @param path
 * @param redirectAbsoluteUrl
 * @param options
 */
export const push = ({
    redirect,
    path: absoluteOrRelativePath,
    redirectAbsoluteUrl,
    redirectToBeekeeper,
    customerSlug,
    instanceSlug,
    origin = window.location.origin,
    replace,
    options,
}: PushOptions) => {
    /**
     * If the URL is internal (meaning either relative or the same origin as the current URL), we handle the redirection using the router.
     * Otherwise, we redirect to the absolute URL.
     */
    if (isInternalUrl(absoluteOrRelativePath, origin)) {
        const path = addFrontVersionToUrl(
            absoluteOrRelativePath.startsWith('http')
                ? absoluteOrRelativePath.replace(origin, '')
                : absoluteOrRelativePath,
        );

        /**
         * When the URL is internal, we need to check if it's a Beekeeper URL or not.
         * If it's a Beekeeper URL, we need to check if the route is supported in the current application.
         * If it's supported, we redirect using the router.
         * If it's not supported, we redirect to Beekeeper.
         */
        const { isBkprPath, pathWithoutBasePath, pathWithoutNoAppsPrefix, targetAppId } = parseUrl(
            path,
            instanceSlug,
            customerSlug,
        );

        /**
         * Finally, we can get the relative route to use with the router
         * We also make sure that the app prefix is there on the URL, as the router
         * expects the URL to have the app prefix (e.g. /ls/, /admin/, etc.)
         */
        const relativeRoute = getMappedRoute(targetAppId, pathWithoutBasePath, pathWithoutNoAppsPrefix);

        if (isBkprPath) {
            /**
             * We also want to make sure that the supported route exists in the target application.
             */
            const { supportedRoute, doesSupportedRouteExistInTargetApp } = doesUrlHaveAMappedUrl(
                path,
                pathWithoutNoAppsPrefix,
                targetAppId,
            );

            /**
             * If the route is supported in the current application and it exists on the target app, we redirect using the router.
             */
            if (supportedRoute && doesSupportedRouteExistInTargetApp) {
                redirect(relativeRoute, replace ? { replace: true } : undefined);
            } else {
                redirectToBeekeeper(
                    addQueryParamsToUrl(
                        APPS_PREFIX_MAPPINGS[targetAppId]
                            ? APPS_PREFIX_MAPPINGS[targetAppId](pathWithoutNoAppsPrefix)
                            : pathWithoutNoAppsPrefix,
                        { external_app: ExternalAppId.la },
                    ),
                    options,
                );
            }
        } else {
            redirect(relativeRoute, replace ? { replace: true } : undefined);
        }
    } else {
        redirectAbsoluteUrl(absoluteOrRelativePath, options);
    }
};

/**
 * Redirects to a Beekeeper URL using the tenant URL from the Beekeeper context.
 * @param url
 * @param options
 */
export const redirectToBeekeeper = (url: string, options?: NavigationOptions) => {
    const pathToUse = `${window.BeekeeperContext?.tenantUrl}${url.replace(`${BKPR_PREFIX}/`, '')}`;
    redirectAbsoluteUrl(pathToUse, options);
};

/**
 * Retrieves the full URL for a given app and url.
 * @param appId
 * @param url
 * @param instanceSlug
 * @returns string
 */
export const getUrl = ({
    appId,
    url,
    instanceSlug,
    customerSlug,
    options,
}: {
    appId: AppId;
    url: string;
    instanceSlug: string;
    customerSlug: string;
    options?: GetURLOptions;
}) => {
    const normalizedUrl = url.startsWith('/') ? url : `/${url}`;

    const appBasePath = `/${instanceSlug}/${URL_PREFIX_BY_APP_ID[appId] || URL_PREFIX}`;

    const basePath = `${appBasePath}${normalizedUrl}`;

    if (options?.type === 'absolute') {
        if (isDev) {
            return `${window.location.origin}/a/${customerSlug}${basePath}`;
        }

        return `${window.location.origin}${basePath}`;
    }

    return basePath;
};

/** Defines the router bridge to be used from web components. */
export const initRouterBridge = async ({
    router,
    getCurrentRoute,
    instanceSlug,
    customerSlug,
    isDevEnv = isDev,
}: InitRouterBridgeProps) => {
    const {
        redirect,
        registerOnAfterRedirect,
        registerOnBeforeRedirect,
        unregisterOnAfterRedirect,
        unregisterOnBeforeRedirect,
    } = router;

    const commonBasePath = `/${instanceSlug}/${URL_PREFIX_BY_APP_ID[applicationId] || URL_PREFIX}`;
    const basePath = isDevEnv ? `/a/${customerSlug}${commonBasePath}` : commonBasePath;

    if (!window.BeekeeperRouterBridge) {
        window.BeekeeperRouterBridge = {
            version: '1.0.0',
            basePath,
            getUrl: (appId: AppId, url: string, options) => getUrl({ appId, url, instanceSlug, customerSlug, options }),
            push: (path, options) =>
                push({
                    redirect,
                    path,
                    redirectAbsoluteUrl,
                    redirectToBeekeeper,
                    instanceSlug,
                    customerSlug,
                    replace: false,
                    options,
                }),
            go: (n) => {
                window.history.go(n);
            },
            replace: (path) =>
                push({
                    redirect,
                    path,
                    redirectAbsoluteUrl: () => document.location.replace(path),
                    redirectToBeekeeper,
                    instanceSlug,
                    customerSlug,
                    replace: true,
                }),
            isInternalUrl,
            currentRoute: () => {
                const { currentRoute } = getCurrentRoute();
                return mapToBeekeeperRoute(currentRoute);
            },
            onBeforeRouteUpdate: (hook) => {
                const wrappedHook: OnBeforeRouteHook = (to, from, next) =>
                    hook(mapToBeekeeperRoute(to), mapToBeekeeperRoute(from), next);

                if (registerOnBeforeRedirect) {
                    registerOnBeforeRedirect(wrappedHook);
                }

                return {
                    stop: () => {
                        if (unregisterOnBeforeRedirect) {
                            unregisterOnBeforeRedirect(wrappedHook);
                        }
                    },
                };
            },
            onAfterRouterUpdate: (hook) => {
                const wrappedHook: OnAfterRouteHook = (to, from) =>
                    hook(mapToBeekeeperRoute(to), mapToBeekeeperRoute(from));

                if (registerOnAfterRedirect) {
                    registerOnAfterRedirect(wrappedHook);
                }

                return {
                    stop: () => {
                        if (unregisterOnAfterRedirect) {
                            unregisterOnAfterRedirect(wrappedHook);
                        }
                    },
                };
            },
        };
    }

    return window.BeekeeperRouterBridge;
};
